import "./OpportunityIntelligence.scss";
import React from "react";
import {observer} from "mobx-react-lite";
import {Box, Button, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import {
    FileDownloadOutlined,
    InsightsOutlined,
    LayersOutlined,
    SsidChartOutlined,
    WarningAmberOutlined,
} from "@mui/icons-material";
import {useGridApiRef} from "@mui/x-data-grid-premium";
import {MainPageFacade} from "../../components/main/page/MainPageFacade.tsx";
import {CARD_CONFIG} from "../../components/dashboard/DashboardCardConfigs.tsx";
import {KpiBoxedValues} from "../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import type {KpiBoxedValue} from "../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import {OpportunityTable} from "./components/OpportunityTable";
import {BySourceView} from "./components/BySourceView";
import {ByPressureView} from "./components/ByPressureView";
import {LifecycleView} from "./components/LifecycleView";
import {
    getBudgetLines,
    getLiveOpportunities,
    getOpportunities,
    OPPORTUNITY_KPIS,
    OPPORTUNITY_SUBTITLE,
} from "./opportunityIntelligenceDemoData";
import {formatCompactCurrency} from "./opportunityUtils";
import {exportOpportunitiesCsv} from "./opportunityExport";

type OppMode = 'realization' | 'pressure' | 'source' | 'lifecycle';

const MODES: { value: OppMode; label: string; icon: React.ReactNode }[] = [
    {value: 'realization', label: 'Realization', icon: <InsightsOutlined fontSize="small"/>},
    {value: 'pressure', label: 'By pressure', icon: <WarningAmberOutlined fontSize="small"/>},
    {value: 'source', label: 'By source', icon: <SsidChartOutlined fontSize="small"/>},
    {value: 'lifecycle', label: 'Lifecycle', icon: <LayersOutlined fontSize="small"/>},
];

export const OpportunityIntelligencePage: React.FC = observer(() => {
    const apiRef = useGridApiRef();
    const [mode, setMode] = React.useState<OppMode>('realization');

    const allOpps = React.useMemo(() => getOpportunities(), []);
    const liveOpps = React.useMemo(() => getLiveOpportunities(), []);
    const budgetLines = React.useMemo(() => getBudgetLines(), []);
    const k = OPPORTUNITY_KPIS;

    const kpis: KpiBoxedValue[] = [
        {key: 'identified', title: 'Identified value', subtitle: formatCompactCurrency(k.identifiedValue, k.currency)},
        {key: 'realized', title: 'Realized value', subtitle: formatCompactCurrency(k.realizedValue, k.currency)},
        {key: 'rate', title: 'Realization rate', subtitle: `${Math.round(k.realizationRate * 100)}%`},
        {key: 'execution', title: 'In execution', subtitle: formatCompactCurrency(k.inExecutionValue, k.currency)},
    ];

    const modeSelector = (
        <Box className="oi-modes" display="flex" alignItems="center" gap={1.5}>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                onChange={(_e, v) => v && setMode(v)}>
                {MODES.map(m => (
                    <ToggleButton key={m.value} value={m.value}>
                        {m.icon}{m.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadOutlined/>}
                onClick={() => exportOpportunitiesCsv(allOpps)}>
                Export
            </Button>
        </Box>
    );

    return <MainPageFacade
        title={CARD_CONFIG['opportunity-intelligence'].title}
        className="opportunity-intelligence-page"
        actionBarEnd={modeSelector}>

        <Typography className="oi-subtitle" variant="body2">
            {OPPORTUNITY_SUBTITLE}
        </Typography>

        <KpiBoxedValues values={kpis}/>

        {mode === 'realization' &&
            <Box className="oi-table-card">
                <Box className="oi-table-card-header">
                    <Typography variant="h2" sx={{fontSize: 18, fontWeight: 700}}>
                        All live opportunities ({liveOpps.length})
                    </Typography>
                    <span className="oi-table-card-hint">
                        {k.abandonedHidden} abandoned hidden · Click a funnel stage to filter, or any row to open it.
                    </span>
                </Box>
                <OpportunityTable rows={liveOpps} apiRef={apiRef}/>
            </Box>
        }

        {mode === 'pressure' && <ByPressureView rows={allOpps} budgetLines={budgetLines}/>}
        {mode === 'source' && <BySourceView rows={allOpps}/>}
        {mode === 'lifecycle' && <LifecycleView rows={allOpps}/>}
    </MainPageFacade>;
});
